import React, {useState} from 'react'
import {fetchAuthSession, signIn, signOut} from 'aws-amplify/auth'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import clsx from 'clsx'
import {getLogger} from '../../services/logging'
import {Input} from 'react-daisyui'

export interface MemberLoginFormProps {
  onLoginSuccess: (token: string) => void
  onLoginFailure: () => void
  onConfirmSignUp?: (email: string) => void
}

interface MemberLoginFormState {
  email: string
  password: string
  isAuthenticating?: boolean
}

const logger = getLogger('MemberLoginForm')

const MemberLoginForm = ({
  onLoginSuccess,
  onLoginFailure,
  onConfirmSignUp
}: MemberLoginFormProps) => {
  const [loginState, setLoginState] = useState<MemberLoginFormState>({
    email: '',
    password: '',
    isAuthenticating: false
  })

  const [isValid, setIsValid] = useState(false)

  const handleLogin = async (email?: string, password?: string) => {
    // Use provided values (from form submit) or state values (from onChange)
    const finalEmail = email || loginState.email
    const finalPassword = password || loginState.password

    setLoginState({
      ...loginState,
      isAuthenticating: true
    })

    const caseInsensitiveEmail = finalEmail.toLowerCase()

    try {
      await signOut()
      const response = await signIn({
        username: caseInsensitiveEmail,
        password: finalPassword
      })
      const {accessToken, idToken} = (await fetchAuthSession()).tokens ?? {}
      logger.info({
        message: 'Member login success',
        data: {response}
      })
      if (!response.isSignedIn) {
        if (response.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
          logger.info({
            message: 'Member login requires confirmation',
            data: {response}
          })
          // Handle confirmation step if needed
          setLoginState({
            ...loginState,
            isAuthenticating: false
          })
          if (onConfirmSignUp) {
            onConfirmSignUp(caseInsensitiveEmail)
          }
          return
        }
      } else {
        onLoginSuccess(idToken.toString())
      }
    } catch (error) {
      logger.error({
        message: 'Member login error',
        errorData: {error},
        data: {email: caseInsensitiveEmail}
      })

      setLoginState({
        ...loginState,
        isAuthenticating: false
      })

      onLoginFailure()
    }
  }

  return (
    <>
      <form
        className="flex flex-col gap-4 w-full"
        onSubmit={e => {
          e.preventDefault()
          // Check actual form values to support autofill
          const email = e.currentTarget.email.value.trim()
          const password = e.currentTarget.password.value.trim()
          if (email && password && !loginState.isAuthenticating) {
            handleLogin(email, password)
          }
        }}
      >
        <Input
          type="text"
          placeholder="Email"
          className="input-form border"
          value={loginState.email}
          name="email"
          onFocus={() =>
            setIsValid(
              !!loginState.password.length && !!loginState.email.length
            )
          }
          onChange={e => {
            const email = e.currentTarget.value.trim()
            setIsValid(!!email.length && !!loginState.password.length)
            setLoginState({...loginState, email})
          }}
        />
        <Input
          type="password"
          placeholder="Password"
          className="input-form border"
          value={loginState.password}
          name="password"
          onFocus={() =>
            setIsValid(
              !!loginState.password.length && !!loginState.email.length
            )
          }
          onChange={e => {
            const password = e.currentTarget.value.trim()
            setIsValid(!!loginState.email.length && !!password.length)
            setLoginState({...loginState, password})
          }}
        />

        <button
          type="submit"
          className="btn btn-secondary  disabled:text-white flex flex-row justify-center items-center w-full gap-2 mt-6"
          disabled={!isValid || loginState.isAuthenticating}
        >
          <div className={clsx({hidden: !loginState.isAuthenticating})}>
            <LoadingSpinner size={LoadingSpinnerSize.Small} />
          </div>
          <div className="text-center">
            {loginState.isAuthenticating ? 'logging in...' : 'sign in'}
          </div>
        </button>
      </form>
    </>
  )
}

export default MemberLoginForm
